import type { ReactNode } from 'react';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import type {
  LoanReviewAgentSlice,
  LoanReviewExternalFinding,
  LoanReviewExternalSubscore,
  LoanReviewPipelineStage,
  LoanReviewViewModel,
} from '../../lib/loanReview/mapLoanReviewToViewModel';
import { stageChipColor } from './loanReviewChipColors';
import { EXTERNAL_ANCHOR_ID, anchorIdFor, scrollToDomain } from './loanReviewAnchors';
import { ADJUSTER_SKIP_REASON } from '../../lib/loanReview/mapLoanReviewToViewModel';
import { ApplicationSectionCard } from '../application/applicationSectionChrome';
import { LoanReviewServiceTable } from './LoanReviewServiceTable';

/** Employer-panel-style label/value grid row, shared by Agent panels. */
function KvRow({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'minmax(120px, 150px) 1fr' },
        columnGap: 1.25,
        rowGap: 0.25,
        py: 0.6,
        alignItems: 'baseline',
      }}
    >
      <Typography component="dt" sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary', lineHeight: 1.4 }}>
        {label}
      </Typography>
      <Box
        component="dd"
        sx={{
          m: 0,
          fontSize: mono ? '0.8125rem' : '0.875rem',
          fontWeight: 500,
          color: 'text.primary',
          lineHeight: 1.45,
          wordBreak: mono ? 'break-all' : 'break-word',
          fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' : 'inherit',
        }}
      >
        {value}
      </Box>
    </Box>
  );
}

const IMPACT_COLOR: Record<string, 'error' | 'warning' | 'default'> = {
  // SEVERE and MINOR are Agent 2's ends of the scale; Agent 3 only ever emits the
  // middle three. Unused keys are harmless, a missing one renders grey and loses
  // the severity signal.
  SEVERE: 'error',
  HIGH: 'error',
  MEDIUM: 'warning',
  LOW: 'default',
  MINOR: 'default',
};

const TIER_LABEL: Record<string, string> = {
  OFFICIAL: 'Official source',
  REPUTABLE_MEDIA: 'News source',
  USER_GENERATED: 'Forum / review site',
  UNKNOWN: 'Unclassified source',
};

/**
 * The headline for a finding, or for a group of findings that are one story.
 *
 * The scorer detects several symptoms of the same problem separately — bounced
 * payments, a broken payment plan and rollover reliance are three codes but ONE
 * customer struggling with payday loans — and then charges only the worst,
 * internally. That internal rule was previously surfaced as "also found, but not
 * counted again", which forced a reviewer to reason about our deduplication
 * logic in order to read the customer's record.
 *
 * Instead, symptoms that belong together share a headline and are listed under
 * it as evidence, with ONE point figure for the group. Nothing is hidden, the
 * point total is the truth, and the word "counted" never appears.
 */
const HISTORY_GROUP_TITLE: Record<string, string> = {
  INTERNAL_PAYDAY_BOUNCE: 'Struggled to repay payday loans',
  INTERNAL_PAYDAY_BROKEN_ARRANGEMENT: 'Struggled to repay payday loans',
  INTERNAL_PAYDAY_ROLLOVER_RELIANCE: 'Struggled to repay payday loans',
  INTERNAL_PAYDAY_CHRONIC_LATE: 'Struggled to repay payday loans',
  INTERNAL_INSTALLMENT_BOUNCE: 'Struggled to repay payday loans',
  INTERNAL_DELINQUENCY_ETD90: 'Fell behind on payments in the past',
  INTERNAL_DELINQUENCY_ETD60: 'Fell behind on payments in the past',
  INTERNAL_DELINQUENCY_ETD45: 'Fell behind on payments in the past',
  INTERNAL_DELINQUENCY_ETD30: 'Fell behind on payments in the past',
  CURRENT_INTERNAL_DELINQUENCY: 'Behind on payments with us right now',
};

/**
 * What a single repayment-history finding means, as a sentence.
 *
 * Used for the codes that stand alone, and as the fallback headline for anything
 * missing from HISTORY_GROUP_TITLE. A de-underscored code ("Payday broken
 * arrangement") is not English and cannot be acted on — every finding here is
 * written as something the customer DID.
 */
const HISTORY_FINDING_TITLE: Record<string, string> = {
  INTERNAL_CHARGE_OFF: 'A loan was written off — we never got the money back',
  CURRENT_INTERNAL_DELINQUENCY: 'Behind on payments with us right now',
  INTERNAL_FIRST_PAYMENT_DEFAULT: 'Missed the very first payment on a loan',
  INTERNAL_DELINQUENCY_ETD90: 'Fell 90+ days behind on a past loan',
  INTERNAL_DELINQUENCY_ETD60: 'Fell 60+ days behind on a past loan',
  INTERNAL_DELINQUENCY_ETD45: 'Fell 45+ days behind on a past loan',
  INTERNAL_DELINQUENCY_ETD30: 'Fell 30+ days behind on a past loan',
  INTERNAL_PAYDAY_BOUNCE: 'Payday loan payments bounced',
  INTERNAL_PAYDAY_PAST_DUE: 'A payday loan is overdue right now',
  INTERNAL_PAYDAY_COLLECTIONS:
    'A payday account reached collections, bankruptcy or legal action',
  INTERNAL_PAYDAY_BROKEN_ARRANGEMENT:
    'Agreed a payment plan on a payday loan, then broke it',
  INTERNAL_PAYDAY_ROLLOVER_RELIANCE:
    'Kept rolling payday loans over instead of clearing them',
  INTERNAL_PAYDAY_CHRONIC_LATE: 'Usually paid payday loans late',
  INTERNAL_INSTALLMENT_BOUNCE: 'A loan payment bounced',
  INTERNAL_REFINANCE_LADDER: 'Refinanced repeatedly rather than paying down',
};

function historyFindingTitle(code: string): string {
  return HISTORY_FINDING_TITLE[code] ?? humaniseCode(code);
}

function historyGroupTitle(code: string): string {
  return HISTORY_GROUP_TITLE[code] ?? historyFindingTitle(code);
}

/**
 * One story per group: the finding that scored, plus the symptoms it absorbed.
 *
 * The pairing already exists in the data — a symptom carries `supersededBy`
 * naming the finding that covered it — so no schema change is needed to group
 * them, and a symptom whose partner is somehow absent still stands on its own
 * rather than vanishing.
 */
function groupHistoryFindings(
  findings: NonNullable<LoanReviewAgentSlice['historyFindings']>,
): Array<{
  key: string;
  title: string;
  points: number;
  basePoints: number | null;
  ageMonths: number | null | undefined;
  evidence: string[];
}> {
  const scored = findings.filter((f) => !f.supersededBy);
  const absorbed = findings.filter((f) => f.supersededBy);
  const claimed = new Set<string>();

  const groups = scored.map((f) => {
    const mine = absorbed.filter((s) => s.supersededBy === f.code);
    mine.forEach((s) => claimed.add(s.code));
    return {
      key: f.code,
      title: historyGroupTitle(f.code),
      points: Math.round(f.deduction),
      basePoints: f.baseDeduction != null ? Math.round(f.baseDeduction) : null,
      ageMonths: f.ageMonths,
      evidence: [f, ...mine].map((e) => e.detail || historyFindingTitle(e.code)),
    };
  });

  // Orphans: a symptom naming a finding that is not in the list. Should not
  // happen, but rendering nothing would silently drop a real adverse fact.
  absorbed
    .filter((s) => !claimed.has(s.code))
    .forEach((s) =>
      groups.push({
        key: s.code,
        title: historyGroupTitle(s.code),
        points: 0,
        basePoints: null,
        ageMonths: s.ageMonths,
        evidence: [s.detail || historyFindingTitle(s.code)],
      }),
    );

  return groups;
}

/** "EMPLOYER_MASS_LAYOFF" → "Mass layoff", "INTERNAL_CHARGE_OFF" → "Charge off" */
function humaniseCode(code: string): string {
  const words = code.replace(/^(EMPLOYER|INTERNAL)_/, '').replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Months → the phrase a reviewer would say out loud.
 *
 * Deliberately vaguer than the number it is given: the underlying record has a
 * real date, but this panel is about explaining a score, and "about 8 years ago"
 * is the right resolution for that. 0 is "this month", not "0 months ago".
 */
function describeAgeMonths(months: number): string {
  if (months <= 0) return 'this month';
  if (months === 1) return 'last month';
  if (months < 12) return `${months} months ago`;
  const years = Math.round(months / 12);
  if (years === 1) return 'about a year ago';
  return `about ${years} years ago`;
}

function formatDate(iso: string | null): string {
  if (!iso) return 'undated';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Split a market-context summary into bullet points.
 *
 * Agent 3 is now prompted to emit one "- " bullet per line, but evaluations
 * persisted before that prompt change hold a paragraph, and reviewers still open
 * those. So: use the model's own bullets when they are there, and otherwise split
 * the paragraph on sentence boundaries. Either way the panel shows points, which
 * is the whole reason for this — a six-sentence block gets skipped.
 */
function toBullets(text: string | null | undefined): string[] {
  const raw = (text ?? '').trim();
  if (!raw) return [];

  const lines = raw
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const marked = lines
    .filter((l) => /^[-•*–]\s+/.test(l))
    .map((l) => l.replace(/^[-•*–]\s+/, '').trim())
    .filter(Boolean);
  if (marked.length) return marked.slice(0, 6);

  // Legacy paragraph. Split after . ! ? followed by a space + capital, so decimals
  // ("2.75%") and abbreviations mid-number survive intact.
  return raw
    .split(/(?<=[.!?])\s+(?=[A-Z(])/)
    .map((t) => t.trim())
    .filter(Boolean)
    .slice(0, 6);
}

function BulletList({ items }: { items: string[] }) {
  return (
    <Box component="ul" sx={{ m: 0, mt: 0.25, pl: 2.25 }}>
      {items.map((t, i) => (
        <Typography key={`${i}-${t.slice(0, 24)}`} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.15 }}>
          {t}
        </Typography>
      ))}
    </Box>
  );
}

/**
 * The arithmetic behind the external subscore, line by line.
 *
 * Two HIGH findings are 100 − 40 − 40 = 20, and the panel now says 20. There used
 * to be a floor of 30 here, which made the displayed score disagree with the
 * deductions listed right above it. The domain weight already caps what this can
 * cost the composite, so the floor was a second cap that bought ~0.8 points and
 * cost the reviewer the ability to check the number.
 *
 * The only remaining clamp is at 0, for the case where deductions exceed 100.
 */
function ScoreWorking({
  findings,
  subscore,
}: {
  findings: LoanReviewExternalFinding[];
  subscore: LoanReviewExternalSubscore;
}) {
  const rows: Array<{ text: string; value: string }> = [
    { text: 'Clean employer baseline', value: '100' },
  ];
  findings.forEach((f) => {
    if (!f.deduction) return;
    rows.push({ text: `${humaniseCode(f.code)} (${f.impact})`, value: `−${f.deduction}` });
  });

  // Only fires when the deductions exceed 100 — a score cannot go negative.
  const raw = subscore.rawScore;
  const floor = subscore.floor ?? 0;
  if (raw != null && raw < floor) {
    rows.push({ text: `Deductions exceed 100 — subtotal ${raw} held at ${floor}`, value: `${floor}` });
  }

  return (
    <Box
      component="details"
      sx={{
        mb: 0.75,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        px: 1,
        py: 0.5,
        '& summary': { cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' },
      }}
    >
      <Box component="summary">
        {/* The adjustment is the number Agent 4's card shows, so name it the same
            way here — a reviewer moving between the two panels should see one
            figure, not two that need reconciling. */}
        {subscore.counted && subscore.adjustment
          ? `How this reaches ${subscore.score}/100 — moves the final score by ${
              subscore.adjustment > 0 ? '+' : '−'
            }${Math.abs(subscore.adjustment).toFixed(1)}`
          : `How this reaches ${subscore.score}/100`}
      </Box>
      <Stack spacing={0.15} sx={{ mt: 0.5 }}>
        {rows.map((r) => (
          <Stack key={r.text} direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="caption" color="text.secondary">
              {r.text}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {r.value}
            </Typography>
          </Stack>
        ))}
        <Divider sx={{ my: 0.35 }} />
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            External context score
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {`${subscore.score}/100`}
          </Typography>
        </Stack>
        {subscore.counted ? (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.35 }}>
            {`Worth ${subscore.weight} points of the final score. A clean employer keeps all ${
              subscore.weight
            }; scoring ${subscore.score}/100 gives up ${Math.abs(
              subscore.adjustment ?? 0,
            ).toFixed(1)} of them, taking Agent 1's ${subscore.agent1Score ?? '—'} to ${
              subscore.compositeScore ?? '—'
            }.`}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.35 }}>
            {ADJUSTER_SKIP_REASON[subscore.notCountedReason ?? ''] ??
              'Computed and recorded, but not applied to the final score.'}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

/**
 * Agent 3's scored findings.
 *
 * Leads with what it did to the decision, then lists each finding as a point with
 * its own proof. A reviewer's job here is to judge whether the source really refers
 * to this applicant's employer, so the date, publisher, quote and match confidence
 * are all on screen rather than behind a click.
 */
/**
 * How the repayment score was reached, laid out like a receipt.
 *
 * A prose sentence ("this would normally cost 30 points, reduced to 9 because…")
 * makes the reader hold three numbers in their head and do the arithmetic. A
 * receipt does not: the starting point, each deduction, each age reduction and
 * the total sit in one column, and the figures add up on screen.
 *
 * The age line is shown as a credit back rather than folded into the deduction
 * because "we charged 30 and then gave 21 back because it was 8 years ago" is
 * the actual policy, and hiding it behind a single net figure is what made the
 * previous version impossible to argue with.
 */
function ScoreReceipt({
  groups,
  subscore,
  effect,
}: {
  groups: ReturnType<typeof groupHistoryFindings>;
  subscore: number;
  effect: LoanReviewAgentSlice['historyEffect'];
}) {
  const row = (label: string, value: string, strong = false) => (
    <Stack
      key={label}
      direction="row"
      justifyContent="space-between"
      alignItems="baseline"
      spacing={1}
      sx={{ py: 0.25 }}
    >
      <Typography variant="caption" sx={{ fontWeight: strong ? 700 : 400 }} color={strong ? 'text.primary' : 'text.secondary'}>
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{ fontWeight: strong ? 700 : 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
      >
        {value}
      </Typography>
    </Stack>
  );

  return (
    <Box sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.25 }}>
        How this score was worked out
      </Typography>
      {row('Every customer starts at', '100')}
      {groups.map((g) => {
        const charged = g.basePoints ?? g.points;
        const forgiven = charged - g.points;
        return (
          <Box key={g.key}>
            {row(g.title, `−${charged}`)}
            {forgiven > 0
              ? row(
                  `Less, because that was ${
                    g.ageMonths != null ? describeAgeMonths(g.ageMonths) : 'a long time ago'
                  }`,
                  `+${forgiven}`,
                )
              : null}
          </Box>
        );
      })}
      <Divider sx={{ my: 0.5 }} />
      {row('Repayment score', `${subscore.toFixed(0)} out of 100`, true)}

      {/* The 0-100 repayment score and the final-score effect are different
          scales, and showing only the first makes the Agent 4 card look like it
          contradicts this one. Shown as the arithmetic rather than a sentence
          explaining the arithmetic — three figures that visibly produce the
          fourth answer the question without anyone having to read a paragraph. */}
      {effect?.counted && effect.weight ? (
        <>
          <Divider sx={{ my: 0.5 }} />
          {row('Points lost on repayment history', `${(100 - subscore).toFixed(0)} of 100`)}
          {row('Repayment history is worth', `${effect.weight} of the final 100`)}
          {row(
            `Effect on the final score  (${(100 - subscore).toFixed(0)} ÷ 100 × ${effect.weight})`,
            `${effect.adjustment > 0 ? '+' : ''}${effect.adjustment.toFixed(1)}`,
            true,
          )}
        </>
      ) : effect && !effect.counted ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          {`This score did not affect the final decision — ${
            ADJUSTER_SKIP_REASON[effect.reason ?? ''] ?? 'it was not counted'
          }.`}
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * Agent 2's findings on the customer's own record.
 *
 * Written for the business team deciding whether to fund, so the layout follows
 * what they need in order: what we found and what it cost, then the reasoning,
 * then the background.
 *
 * Two deliberate structural choices:
 *
 * 1. Findings that scored ZERO are NOT in the main list. They are real (an event
 *    we detected and chose not to charge twice), so they cannot be deleted — the
 *    subscore would look arbitrary without them. But putting them inline next to
 *    the ones that actually cost points makes a reviewer ask "so did this matter
 *    or not?" on every row. They live in the collapsed section, under a heading
 *    that says plainly why they scored nothing.
 *
 * 2. Every number is stated as points, in words, next to what it applied to.
 *    "×0.30" and "MEDIUM" are how the scorer thinks; neither means anything to
 *    the person deciding on the loan.
 *
 * NOT loan-level detail: loan ids, amounts and exact dates are dropped before
 * this payload leaves the agent. A reviewer needing those opens the servicing
 * record.
 */
function HistoryFindings({
  findings,
  context,
  working,
  effect,
}: {
  findings: NonNullable<LoanReviewAgentSlice['historyFindings']>;
  context: NonNullable<LoanReviewAgentSlice['historyContext']>;
  working: LoanReviewAgentSlice['historyScoreWorking'];
  effect: LoanReviewAgentSlice['historyEffect'];
}) {
  const groups = groupHistoryFindings(findings);
  if (!groups.length && !context.length) return null;

  const pts = (n: number) => (n === 1 ? '1 point' : `${n} points`);

  return (
    <Box data-testid="loan-review-history-findings">
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
        What we found
      </Typography>

      {groups.length ? (
        <Stack spacing={0.75}>
          {groups.map((g) => {
            return (
              <Box
                key={g.key}
                sx={{ p: 0.75, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
              >
                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    color={g.points >= 40 ? 'error' : g.points > 0 ? 'warning' : 'default'}
                    label={`−${pts(g.points)}`}
                    sx={{ height: 18, fontSize: '0.625rem', fontWeight: 700 }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {g.title}
                  </Typography>
                  {g.ageMonths != null ? (
                    <Typography variant="caption" color="text.secondary">
                      {describeAgeMonths(g.ageMonths)}
                    </Typography>
                  ) : null}
                </Stack>
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.25 }}>
                  {g.evidence.map((line) => (
                    <Typography
                      key={line}
                      component="li"
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'list-item' }}
                    >
                      {line}
                    </Typography>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Nothing concerning on this customer&apos;s record with us.
        </Typography>
      )}

      {working ? (
        <ScoreReceipt groups={groups} subscore={working.subscore} effect={effect} />
      ) : null}

      {context.length ? (
        <Box
          component="details"
          sx={{
            mt: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            px: 1,
            py: 0.5,
            '& summary': { cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 },
          }}
        >
          <Box component="summary" sx={{ color: 'text.secondary' }}>
            Everything they have borrowed from us
          </Box>
          <Box component="dl" sx={{ m: 0, mt: 0.75 }}>
            {context.map((row, i) => (
              <Box key={row.label}>
                {i > 0 ? <Divider /> : null}
                <KvRow label={row.label} value={row.value} />
              </Box>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            To see individual loans, dates and amounts, open the customer&apos;s
            servicing record.
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

function ExternalFindings({
  findings,
  subscore,
}: {
  findings: LoanReviewExternalFinding[];
  subscore: LoanReviewExternalSubscore;
}) {
  const notResearched = subscore.gateStatus === 'UNAVAILABLE';

  return (
    <Box id={EXTERNAL_ANCHOR_ID} data-testid="loan-review-external-findings" sx={{ scrollMarginTop: 96 }}>
      <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
          External findings
        </Typography>
        {notResearched ? null : (
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {`${subscore.score ?? '—'}/100`}
          </Typography>
        )}
        {!notResearched && !subscore.counted ? (
          <Chip
            size="small"
            variant="outlined"
            label="not counted (weight 0)"
            sx={{ height: 18, fontSize: '0.625rem' }}
          />
        ) : null}
      </Stack>

      {notResearched ? (
        /* Distinct from "clean": the two used to render identically, which made an
           unresearched employer look like a verified-clean one. */
        <Typography variant="body2" color="text.secondary">
          Not researched — external context was unavailable for this application, so
          it did not affect the decision.
        </Typography>
      ) : findings.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {subscore.discardedCount > 0
            ? `No qualifying findings — ${subscore.discardedCount} considered and discarded as stale, unsourced or about a different company.`
            : 'No adverse news found for this employer in the last 3 months.'}
        </Typography>
      ) : (
        <>
          <ScoreWorking findings={findings} subscore={subscore} />
          <Stack spacing={0.75}>
            {findings.map((f) => (
              <Box
                key={`${f.code}-${f.eventDate ?? ''}-${f.sourceUrl ?? ''}`}
                sx={{ p: 0.75, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
              >
                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    color={IMPACT_COLOR[f.impact] ?? 'default'}
                    label={`${f.impact}${f.deduction ? ` −${f.deduction}` : ''}`}
                    sx={{ height: 18, fontSize: '0.625rem', fontWeight: 700 }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {humaniseCode(f.code)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(f.eventDate)}
                  </Typography>
                </Stack>
                {f.evidenceQuote ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.25, fontStyle: 'italic' }}
                  >
                    “{f.evidenceQuote}”
                  </Typography>
                ) : null}
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mt: 0.25 }}
                >
                  {f.sourceUrl ? (
                    <Typography
                      component="a"
                      href={f.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="caption"
                      sx={{ wordBreak: 'break-all' }}
                    >
                      {f.publisher || f.sourceDomain || 'source'}
                    </Typography>
                  ) : null}
                  {f.sourceTier ? (
                    <Typography variant="caption" color="text.secondary">
                      {TIER_LABEL[f.sourceTier] ?? f.sourceTier}
                    </Typography>
                  ) : null}
                  {f.employerMatchConfidence != null ? (
                    <Typography variant="caption" color="text.secondary">
                      {`employer match ${(f.employerMatchConfidence * 100).toFixed(0)}%`}
                    </Typography>
                  ) : null}
                </Stack>
              </Box>
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
}

/**
 * How the final score was reached: Agent 1, each outside signal, then the total.
 *
 * The arithmetic is shown rather than asserted. A reviewer who subtracts the rows
 * must land on the final number, or the panel loses their trust — which is why
 * uncounted rows show "no effect" instead of being hidden.
 */
function FinalScoreTable({ agent }: { agent: LoanReviewAgentSlice }) {
  const rows = agent.adjustments ?? [];
  if (agent.score == null || agent.finalScore == null) return null;
  return (
    <Box data-testid="loan-review-final-score">
      <Stack spacing={0.15}>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="caption" color="text.secondary">
            Agent 1 — internal evidence
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {agent.score.toFixed(1)}
          </Typography>
        </Stack>
        {rows.map((r) => {
          // The row names a score explained elsewhere on the page; sending the
          // reader there beats making them find it.
          //
          // Deliberately NOT a `document.getElementById` check: this card renders in
          // the same commit as the panel it points at, so on first paint the lookup
          // finds nothing and the link renders dead — then becomes live after any
          // re-render. `scrollToDomain` already no-ops on a missing target, so a
          // map hit is the right condition.
          const jumpable = r.counted && anchorIdFor(r.adjuster) !== null;
          return (
          <Stack key={r.adjuster} direction="row" justifyContent="space-between" spacing={2}>
            <Typography
              variant="caption"
              color={jumpable ? 'primary.main' : 'text.secondary'}
              onClick={jumpable ? () => scrollToDomain(r.adjuster) : undefined}
              sx={jumpable ? { cursor: 'pointer', '&:hover': { textDecoration: 'underline' } } : undefined}
            >
              {r.label}
              {r.counted && r.subscore != null ? (
                <Typography component="span" variant="caption" color="text.disabled">
                  {` · scored ${Math.round(r.subscore)}/100`}
                </Typography>
              ) : null}
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: r.counted ? 700 : 400 }}
              color={!r.counted ? 'text.disabled' : r.adjustment < 0 ? 'warning.main' : 'text.secondary'}
            >
              {r.counted
                ? `${r.adjustment >= 0 ? '+' : '−'}${Math.abs(r.adjustment).toFixed(1)}`
                : ADJUSTER_SKIP_REASON[r.reason ?? ''] ?? 'no effect'}
            </Typography>
          </Stack>
          );
        })}
        <Divider sx={{ my: 0.35 }} />
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="caption" sx={{ fontWeight: 800 }}>
            Final score
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
            {`${agent.finalScore.toFixed(1)}/100`}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}


function AgentPanel({
  title,
  agent,
  emptyHint,
  stage,
  testId,
}: {
  title: string;
  agent: LoanReviewAgentSlice;
  emptyHint: string;
  stage?: LoanReviewPipelineStage;
  testId: string;
}) {
  return (
    <Box
      data-testid={testId}
      sx={{ p: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', minWidth: 0 }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: agent.present ? 1 : 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          {title}
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="center">
          {agent.present && agent.score != null && !agent.adjustments?.length ? (
            <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1 }}>
              {Math.round(agent.score)}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.25 }}>
                /100
              </Typography>
            </Typography>
          ) : null}
          {stage ? (
            <Chip
              size="small"
              color={stageChipColor(stage.status)}
              label={stage.status === 'missing' ? 'Not run' : stage.status}
              sx={{ height: 20, fontSize: '0.6875rem', fontWeight: 700, textTransform: 'capitalize' }}
            />
          ) : null}
        </Stack>
      </Stack>
      {!agent.present ? (
        <Typography variant="body2" color="text.secondary">
          {emptyHint}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {agent.blurb ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
              {agent.blurb}
            </Typography>
          ) : null}
          {agent.adjustments?.length ? <FinalScoreTable agent={agent} /> : null}
          <Box component="dl" sx={{ m: 0 }}>
            {agent.lines.map((line, i) => (
              <Box key={line.label}>
                {i > 0 ? <Divider /> : null}
                <KvRow label={line.label} value={line.value} />
              </Box>
            ))}
          </Box>
          {agent.externalSubscore ? (
            <ExternalFindings
              findings={agent.externalFindings ?? []}
              subscore={agent.externalSubscore}
            />
          ) : null}
          {agent.historyFindings?.length || agent.historyContext?.length ? (
            <HistoryFindings
              findings={agent.historyFindings ?? []}
              context={agent.historyContext ?? []}
              working={agent.historyScoreWorking ?? null}
              effect={agent.historyEffect ?? null}
            />
          ) : null}
          {agent.macroSummary || agent.sectorSummary ? (
            /* Collapsed: useful for a reviewer writing notes, but background next
               to the scored findings above. */
            <Box
              component="details"
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 1,
                py: 0.5,
                '& summary': { cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 },
              }}
            >
              <Box component="summary" sx={{ color: 'text.secondary' }}>
                Market context
              </Box>
              <Stack spacing={1} sx={{ mt: 0.75 }}>
                {agent.macroSummary ? (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Macroeconomics
                    </Typography>
                    <BulletList items={toBullets(agent.macroSummary)} />
                  </Box>
                ) : null}
                {agent.sectorSummary ? (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Sector
                    </Typography>
                    <BulletList items={toBullets(agent.sectorSummary)} />
                  </Box>
                ) : null}
              </Stack>
            </Box>
          ) : agent.summary ? (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                Summary
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {agent.summary}
              </Typography>
            </Box>
          ) : null}
          {(agent.concerns?.length ?? 0) > 0 ? (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {agent.concerns!.map((c) => (
                <Chip key={c} size="small" color="warning" label={c} />
              ))}
            </Stack>
          ) : null}
          {(agent.flags?.length ?? 0) > 0 ? (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {agent.flags!.map((f) => (
                <Chip key={f} size="small" variant="outlined" label={f} />
              ))}
            </Stack>
          ) : null}
        </Stack>
      )}
    </Box>
  );
}

function AgentsSection({ vm }: { vm: LoanReviewViewModel }) {
  const findStage = (id: LoanReviewPipelineStage['id']) => vm.pipelineStages.find((s) => s.id === id);
  const presentCount = [vm.agent1, vm.agent2, vm.agent3, vm.agent4].filter((a) => a.present).length;

  return (
    <ApplicationSectionCard
      title="Automated review agents"
      subtitle={vm.hasPipelineSummary ? `${presentCount} of 4 stages ran` : 'Agent stages unavailable for this run'}
      icon={<SmartToyRoundedIcon fontSize="small" />}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 1.5,
        }}
      >
        <AgentPanel
          title="Agent 1 — Internal evidence"
          agent={vm.agent1}
          stage={findStage('agent1')}
          emptyHint="Agent 1 output was not captured for this run."
          testId="loan-review-agent1"
        />
        <AgentPanel
          title="Agent 2 — Repayment history"
          agent={vm.agent2}
          stage={findStage('agent2')}
          emptyHint="Agent 2 output was not captured for this run."
          testId="loan-review-agent2"
        />
        <AgentPanel
          title="Agent 3 — External / macroeconomics"
          agent={vm.agent3}
          stage={findStage('agent3')}
          emptyHint="Agent 3 output was not captured for this run."
          testId="loan-review-agent3"
        />
        <AgentPanel
          title="Agent 4 — Final decision"
          agent={vm.agent4}
          stage={findStage('agent4')}
          emptyHint="Agent 4 output was not captured for this run."
          testId="loan-review-agent4"
        />
      </Box>
    </ApplicationSectionCard>
  );
}

export function LoanReviewSections({ vm }: { vm: LoanReviewViewModel }) {
  // The per-source cards supersede the dense service tables; only fall back to
  // the tables on older runs that have no source_cards.
  const showLegacyTables = vm.sourceCards.length === 0;
  return (
    <Stack spacing={2} data-testid="loan-review-sections">
      {showLegacyTables
        ? vm.services
            .filter((section) => section.key !== 'cross_check')
            .map((section) => (
            <LoanReviewServiceTable key={section.key} section={section} />
          ))
        : null}
      <AgentsSection vm={vm} />
    </Stack>
  );
}
